/**
 * ============================================================================
 * ReviewCalendar — data layer.
 * Owner: Phase 7 — Management Review Module (Agent F).
 * Purpose: React-Query hook backing the quarterly review calendar. Hits
 *          GET /api/v1/management-reviews and groups items by calendar quarter.
 * URS rows: MGR-001..004.
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { buildHeaders, apiUrl } from '../../api/client';

export type ReviewStatus =
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'HELD'
  | 'MINUTES_DRAFT'
  | 'MINUTES_FINALIZED'
  | 'CLOSED'
  | 'CANCELLED';

export interface ReviewListItem {
  id: string;
  reviewNumber: string;
  title: string;
  periodStartUtc: string;
  periodEndUtc: string;
  scheduledAtUtc: string;
  heldAtUtc?: string | null;
  status: ReviewStatus;
  chairUserId: string;
  attendeeCount: number;
  minutesFinalized: boolean;
}

export interface ReviewCalendarResponse {
  items: ReviewListItem[];
  total: number;
}

export function useReviewCalendarQuery() {
  return useQuery<ReviewCalendarResponse>({
    queryKey: ['management-reviews', 'calendar'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/api/v1/management-reviews'), { headers: buildHeaders() });
      if (!res.ok) throw new Error(`management-reviews ${res.status}`);
      return res.json();
    },
    staleTime: 30_000,
  });
}

/** Group reviews into year-quarter buckets for the calendar grid.
 *
 * Uses periodEndUtc — minus one millisecond to dodge timezone roll-back
 * artefacts (e.g. a period_start_utc of "2026-01-01 00:00+03" lives at
 * UTC "2025-12-31 21:00" which would incorrectly bucket the Q1 review
 * under 2025-Q4). The end of the period is the most reliable anchor:
 * a review of "2026-01-01 → 2026-03-31" always belongs in 2026-Q1.
 */
export function groupByQuarter(items: ReviewListItem[]): Record<string, ReviewListItem[]> {
  const out: Record<string, ReviewListItem[]> = {};
  for (const r of items) {
    const anchor = r.periodEndUtc || r.periodStartUtc;
    const d = new Date(new Date(anchor).getTime() - 1);
    const q = Math.floor(d.getUTCMonth() / 3) + 1;
    const key = `${d.getUTCFullYear()}-Q${q}`;
    if (!out[key]) out[key] = [];
    out[key]!.push(r);
  }
  return out;
}
